// src/stores/nonInfoSecRiskStore.ts
import { proxy } from 'valtio';
import type { NonInfoSecRisk } from '../types/nonInfoSecRisk';
import { api } from '../utils/api';

export type { NonInfoSecRisk };

interface NonInfoSecRiskState {
  risks: NonInfoSecRisk[];
  config: {
    riskCategories: string[];
    treatments: Array<{ treatment: string; description: string }>;
    controlStatusOptions: string[];
    mitigations: Array<{ name: string; description: string }>;
  };
  loading: boolean;
  searchTerm: string;
  categoryFilter: string;
  statusFilter: string;
  treatmentFilter: string;
}

const defaultConfig = {
  riskCategories: [
    'Operational Risk', 'Financial Risk', 'Strategic Risk', 'Compliance Risk',
    'Reputational Risk', 'Health & Safety', 'Environmental Risk', 'Supply Chain Risk',
  ],
  treatments: [
    { treatment: 'Treat the risk', description: 'Implement controls to reduce likelihood or severity' },
    { treatment: 'Avoid the risk', description: 'Stop the activity that causes the risk' },
    { treatment: 'Transfer the risk', description: 'Transfer risk to third party (e.g., insurance)' },
    { treatment: 'Accept the risk', description: 'Accept the risk as it is within tolerance' },
  ],
  controlStatusOptions: ['Not Started', 'In Progress', 'Under Review', 'Implemented', 'Not Applicable'],
  mitigations: [
    { name: 'Process Improvement', description: 'Improve existing processes to reduce risk' },
    { name: 'Training & Awareness', description: 'Educate staff to prevent incidents' },
    { name: 'Monitoring & Detection', description: 'Implement monitoring systems' },
    { name: 'Redundancy & Backup', description: 'Add backup systems or redundancy' },
    { name: 'Insurance', description: 'Transfer risk through insurance coverage' },
    { name: 'Contractual Controls', description: 'Use contracts to manage third-party risks' },
    { name: 'Physical Security', description: 'Enhance physical security measures' },
    { name: 'Custom Mitigation', description: 'Define a custom mitigation approach' },
  ],
};

export const nonInfoSecRiskStore = proxy<NonInfoSecRiskState>({
  risks: [],
  config: { ...defaultConfig },
  loading: true,
  searchTerm: '',
  categoryFilter: 'all',
  statusFilter: 'all',
  treatmentFilter: 'all',
});

// Track last-known server timestamp outside reactive proxy to avoid save loops
let nonInfoSecConfigLastSavedAt = '';

export const nonInfoSecRiskActions = {
  loadAll: async () => {
    try {
      nonInfoSecRiskStore.loading = true;
      const [risks, config] = await Promise.all([
        api.get('/non-infosec-risks') as Promise<NonInfoSecRisk[]>,
        api.get('/non-infosec-risk-config') as Promise<(NonInfoSecRiskState['config'] & { _updatedAt?: string }) | null>,
      ]);
      nonInfoSecRiskStore.risks = risks;
      if (config) {
        // Extract _updatedAt before assigning to store (use !== undefined so '' is handled)
        if (config._updatedAt !== undefined) {
          nonInfoSecConfigLastSavedAt = config._updatedAt || '';
          delete config._updatedAt;
        }
        nonInfoSecRiskStore.config = config;
      }
    } catch (e) {
      console.error('Failed to load non-InfoSec data from API:', e);
      // Fallback to localStorage
      try {
        const stored = localStorage.getItem('nonInfoSecRisks');
        if (stored) nonInfoSecRiskStore.risks = JSON.parse(stored);
        const configStored = localStorage.getItem('nonInfoSecRiskConfig');
        if (configStored) nonInfoSecRiskStore.config = JSON.parse(configStored);
      } catch {}
    } finally {
      nonInfoSecRiskStore.loading = false;
    }
  },

  saveRisks: async () => {
    // No-op: individual CRUD operations handle persistence now
  },

  saveConfig: async () => {
    try {
      const payload = { ...JSON.parse(JSON.stringify(nonInfoSecRiskStore.config)), _updatedAt: nonInfoSecConfigLastSavedAt };
      const response = await api.put('/non-infosec-risk-config', payload) as Record<string, unknown>;
      if (response._updatedAt !== undefined) {
        nonInfoSecConfigLastSavedAt = (response._updatedAt as string) || '';
      }
    } catch (e: unknown) {
      const err = e as Error & { status?: number };
      if (err.status === 409) {
        console.warn('Business risk config conflict detected, reloading...');
        await nonInfoSecRiskActions.loadAll();
      } else {
        console.error('Failed to save config to API:', e);
      }
    }
  },

  addRisk: async (risk: NonInfoSecRisk) => {
    nonInfoSecRiskStore.risks.push(risk);
    try {
      const serverRisk = await api.post('/non-infosec-risks', risk) as NonInfoSecRisk;
      // Replace optimistic entry with server response (has real ID, ref number, timestamps)
      const idx = nonInfoSecRiskStore.risks.findIndex(r => r.id === risk.id);
      if (idx !== -1) {
        nonInfoSecRiskStore.risks[idx] = serverRisk;
      }
    } catch (e) {
      const idx = nonInfoSecRiskStore.risks.findIndex(r => r.id === risk.id);
      if (idx !== -1) nonInfoSecRiskStore.risks.splice(idx, 1);
      console.error('Failed to save risk:', e);
      throw e;
    }
  },

  updateRisk: async (id: string, updates: Partial<NonInfoSecRisk>) => {
    const index = nonInfoSecRiskStore.risks.findIndex(r => r.id === id);
    if (index !== -1) {
      const previous = { ...nonInfoSecRiskStore.risks[index] };
      // Send the store's current updatedAt so the server can use it for optimistic locking.
      // The server will set the new timestamp — do NOT override updatedAt before sending.
      const payload = { ...nonInfoSecRiskStore.risks[index], ...updates, updatedAt: previous.updatedAt, lastModified: previous.lastModified };
      // Optimistic local update with a provisional timestamp
      const now = new Date().toISOString();
      nonInfoSecRiskStore.risks[index] = { ...payload, updatedAt: now, lastModified: now };
      try {
        const serverResponse = await api.put(`/non-infosec-risks/${id}`, payload) as Partial<NonInfoSecRisk>;
        // Update with server-confirmed timestamps
        if (serverResponse.updatedAt) {
          nonInfoSecRiskStore.risks[index] = { ...nonInfoSecRiskStore.risks[index], updatedAt: serverResponse.updatedAt, lastModified: serverResponse.lastModified || serverResponse.updatedAt };
        }
      } catch (e: unknown) {
        nonInfoSecRiskStore.risks[index] = previous;
        const err = e as Error & { status?: number };
        if (err.status === 409) {
          // Conflict — reload latest data from server
          console.warn('Conflict detected on business risk update, reloading...');
          await nonInfoSecRiskActions.loadAll();
        }
        console.error('Failed to update risk:', e);
        throw e;
      }
    }
  },

  deleteRisk: async (id: string) => {
    const index = nonInfoSecRiskStore.risks.findIndex(r => r.id === id);
    if (index !== -1) {
      const removed = nonInfoSecRiskStore.risks[index];
      nonInfoSecRiskStore.risks.splice(index, 1);
      try {
        await api.del(`/non-infosec-risks/${id}`);
      } catch (e) {
        nonInfoSecRiskStore.risks.splice(index, 0, removed);
        console.error('Failed to delete risk:', e);
        throw e;
      }
    }
  },

  getRiskById: (id: string): NonInfoSecRisk | undefined => {
    return nonInfoSecRiskStore.risks.find(r => r.id === id);
  },

  generateReferenceNumber: (): string => {
    const year = new Date().getFullYear();
    const existingNumbers = nonInfoSecRiskStore.risks
      .map(r => r.referenceNumber)
      .filter(ref => ref && ref.startsWith(`BR-${year}-`))
      .map(ref => parseInt(ref.split('-')[2]))
      .filter(num => !isNaN(num));
    const nextNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;
    return `BR-${year}-${String(nextNumber).padStart(4, '0')}`;
  },

  // Filter actions
  setSearchTerm: (term: string) => { nonInfoSecRiskStore.searchTerm = term; },
  setCategoryFilter: (category: string) => { nonInfoSecRiskStore.categoryFilter = category; },
  setStatusFilter: (status: string) => { nonInfoSecRiskStore.statusFilter = status; },
  setTreatmentFilter: (treatment: string) => { nonInfoSecRiskStore.treatmentFilter = treatment; },

  // Config management actions
  addRiskCategory: (category: string) => {
    if (!nonInfoSecRiskStore.config.riskCategories.includes(category)) {
      nonInfoSecRiskStore.config.riskCategories.push(category);
      nonInfoSecRiskActions.saveConfig();
    }
  },
  removeRiskCategory: (category: string) => {
    const index = nonInfoSecRiskStore.config.riskCategories.indexOf(category);
    if (index !== -1) { nonInfoSecRiskStore.config.riskCategories.splice(index, 1); nonInfoSecRiskActions.saveConfig(); }
  },
  reorderRiskCategories: (categories: string[]) => { nonInfoSecRiskStore.config.riskCategories = categories; nonInfoSecRiskActions.saveConfig(); },
  addMitigation: (mitigation: { name: string; description: string }) => { nonInfoSecRiskStore.config.mitigations.push(mitigation); nonInfoSecRiskActions.saveConfig(); },
  updateMitigation: (index: number, mitigation: { name: string; description: string }) => {
    if (index >= 0 && index < nonInfoSecRiskStore.config.mitigations.length) { nonInfoSecRiskStore.config.mitigations[index] = mitigation; nonInfoSecRiskActions.saveConfig(); }
  },
  removeMitigation: (index: number) => {
    if (index >= 0 && index < nonInfoSecRiskStore.config.mitigations.length) { nonInfoSecRiskStore.config.mitigations.splice(index, 1); nonInfoSecRiskActions.saveConfig(); }
  },
  reorderMitigations: (mitigations: Array<{ name: string; description: string }>) => { nonInfoSecRiskStore.config.mitigations = mitigations; nonInfoSecRiskActions.saveConfig(); },
  addTreatment: (treatment: { treatment: string; description: string }) => { nonInfoSecRiskStore.config.treatments.push(treatment); nonInfoSecRiskActions.saveConfig(); },
  updateTreatment: (index: number, treatment: { treatment: string; description: string }) => {
    if (index >= 0 && index < nonInfoSecRiskStore.config.treatments.length) { nonInfoSecRiskStore.config.treatments[index] = treatment; nonInfoSecRiskActions.saveConfig(); }
  },
  removeTreatment: (index: number) => {
    if (index >= 0 && index < nonInfoSecRiskStore.config.treatments.length) { nonInfoSecRiskStore.config.treatments.splice(index, 1); nonInfoSecRiskActions.saveConfig(); }
  },
  reorderTreatments: (treatments: Array<{ treatment: string; description: string }>) => { nonInfoSecRiskStore.config.treatments = treatments; nonInfoSecRiskActions.saveConfig(); },
  resetConfigToDefaults: () => {
    nonInfoSecRiskStore.config = { ...defaultConfig };
    nonInfoSecRiskActions.saveConfig();
  },
};

// Helper functions
import { riskMatrixStore } from './riskMatrixStore';

export const getNonInfoSecRiskLevel = (likelihood: string, severity: string) => {
  const score = calculateNonInfoSecRiskScore(likelihood, severity);
  const likelihoodLabels = ['Rare', 'Unlikely', 'Possible', 'Likely', 'Almost Certain'];
  const severityLabels = ['Insignificant', 'Minor', 'Moderate', 'Serious', 'Catastrophic'];
  const likelihoodIdx = likelihoodLabels.indexOf(likelihood);
  const severityIdx = severityLabels.indexOf(severity);

  let color: 'green' | 'amber' | 'red' = 'green';
  let level: 'Low' | 'Medium' | 'High' = 'Low';

  if (likelihoodIdx >= 0 && severityIdx >= 0 &&
      riskMatrixStore.nonInfoSec &&
      riskMatrixStore.nonInfoSec.colors &&
      riskMatrixStore.nonInfoSec.colors[likelihoodIdx]) {
    const matrixColor = riskMatrixStore.nonInfoSec.colors[likelihoodIdx][severityIdx];
    if (matrixColor) { color = matrixColor; }
  } else {
    if (score <= 4) color = 'green';
    else if (score <= 12) color = 'amber';
    else color = 'red';
  }

  let hexColor = '#16a34a';
  if (color === 'green') { level = 'Low'; hexColor = '#10b981'; }
  else if (color === 'amber') { level = 'Medium'; hexColor = '#f59e0b'; }
  else if (color === 'red') { level = 'High'; hexColor = '#ef4444'; }

  return { level, color: hexColor, score };
};

export const calculateNonInfoSecRiskScore = (likelihood: string, severity: string): number => {
  const likelihoodMap: Record<string, number> = { 'Rare': 1, 'Unlikely': 2, 'Possible': 3, 'Likely': 4, 'Almost Certain': 5 };
  const severityMap: Record<string, number> = { 'Insignificant': 1, 'Minor': 2, 'Moderate': 3, 'Serious': 4, 'Catastrophic': 5 };
  return (likelihoodMap[likelihood] || 3) * (severityMap[severity] || 3);
};
